'use client';

import { ComponentPropsWithoutRef, ElementType, ReactNode } from 'react';

import classNames from 'classnames';
import Image, { StaticImageData } from 'next/image';

import { LinkI18n } from '@/i18n/routing';
import context from '@/styles/contexts.module.scss';

import { WithClassName } from '../../types';
import styles from './ProductCard.module.scss';

export type ProductCardProps<T extends ElementType = typeof LinkI18n> = {
  title: ReactNode;
  enTitle: ReactNode;
  description: ReactNode;
  image: StaticImageData;
  mobileImage?: StaticImageData | null;
  tag?: ReactNode;
  controls?: any;
  component?: T;
  componentProps: Omit<ComponentPropsWithoutRef<T>, 'className'>;
} & WithClassName;

export function ProductSlideCard<T extends ElementType = typeof LinkI18n>({
  isActive,
  isVisible,
  className,
  // @ts-ignore
  component = LinkI18n,
  ...props
}: ProductCardProps<T> & { isActive?: boolean; isVisible?: boolean }) {
  return (
    <ProductCard
      {...(props as ProductCardProps<T>)}
      className={classNames(className, styles.productSlideCard, {
        [styles.active]: isActive,
        [styles.visible]: isVisible,
      })}
    />
  );
}

export const ProductCard = <T extends ElementType = typeof LinkI18n>({
  title,
  enTitle,
  description,
  image,
  mobileImage,
  className,
  tag,
  controls,
  // @ts-ignore
  component = LinkI18n,
  componentProps,
}: ProductCardProps<T>) => {
  const ComponentElement = component as ElementType;
  return (
    <ComponentElement
      className={classNames(styles.productCard, context.light, className)}
      data-event-label={enTitle}
      {...componentProps}
    >
      <div className={classNames(styles.cardInner)}>
        <div className={styles.content}>
          {tag}
          <h3 className={styles.title}>{title}</h3>
          <p className={styles.description}>{description}</p>
        </div>
        <div className={styles.image}>
          <Image
            src={mobileImage ? mobileImage : image}
            alt={''}
            fill
            sizes="100vw"
            placeholder={'blur'}
            draggable={false}
          />
        </div>
      </div>
    </ComponentElement>
  );
};
